import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { asId, type AreaId, type SubAreaId } from '@shared/brand';
import type { Area } from '@domain/entities/Organization';
import {
  PERSON_DOCUMENTS,
  ROLE_LABEL,
  ROLES,
  type PersonDocumentKind,
  type Role,
} from '@domain/vocabulary';
import {
  capitalizeNamePart,
  NAME_PART_LABEL,
  PersonFullName,
  type NamePart,
  type PersonFullNameError,
} from '@domain/value-objects/PersonFullName';
import { personNameMessage } from '@domain/value-objects/PersonName';
import {
  Banner,
  Button,
  Field,
  Modal,
  cx,
  inputClass,
  selectClass,
} from '@presentation/design-system';
import { reasonFor } from '@presentation/format';
import { useRuntimeConfig } from '@presentation/providers/RuntimeConfigProvider';
import { useAccess } from '@presentation/hooks/useAccess';
import { POSITION_ADD_PERMISSIONS } from '@domain/policies/PositionCatalog';
import { PositionPicker } from '../PositionPicker';
import { intakeGaps, NAMED_GAPS, phoneError } from './intakeGaps';
import { useSession } from '@presentation/providers/SessionProvider';
import { useToast } from '@presentation/providers/ToastProvider';
import { suggestEmail, type Availability, type EmailSuggestion } from './suggestEmail';
import { WORK_MODES, WORK_MODE_LABEL, type WorkMode } from '@domain/vocabulary';
import { getServices } from '@infrastructure/services';
import styles from './PersonIntakeModal.module.css';

/**
 * Dar de alta a alguien, que **no es lo mismo que validar**.
 *
 * Validar reacciona a una llegada: la persona ya inició sesión con su correo
 * corporativo, el portal le creó una ficha en `pending`, y un administrador la
 * aprueba. Ese flujo ya existía y es el panel «Cuentas por validar».
 *
 * Esto lo provoca: RR.HH. captura a quien todavía no tiene nada — ni ficha ni
 * cuenta — y de aquí salen las dos. Es el camino que hace falta para las 141
 * personas sin cuenta de Workspace y para las catorce áreas que faltan por abrir,
 * y hasta hoy no existía en ninguna parte.
 *
 * **El nombre se captura en cuatro casillas y el sistema lo arma.** Es la
 * decisión de fondo: partir «María de los Ángeles Ríos» después es adivinar, y
 * capturarlo partido es gratis. Ver `PersonFullName`.
 */

const emptyForm = {
  primerNombre: '',
  segundoNombre: '',
  primerApellido: '',
  segundoApellido: '',
  emailLocalPart: '',
  hireDate: '',
};

type FormState = typeof emptyForm;

/** El mensaje de un fallo del nombre, ya apuntando a la casilla que lo tiene. */
const messageFor = (error: PersonFullNameError): string => {
  switch (error.kind) {
    case 'required':
      return `${NAME_PART_LABEL[error.part]} es obligatorio.`;
    case 'invalid-characters':
      return `${NAME_PART_LABEL[error.part]}: usa sólo letras, sin números ni signos.`;
    case 'composed':
      return personNameMessage(error.cause);
  }
};

/** La casilla señalada, para teñir sólo esa y no las cuatro. */
const partOf = (error: PersonFullNameError | null): NamePart | null =>
  error === null || error.kind === 'composed' ? null : error.part;

export const PersonIntakeModal = ({
  onClose,
  onCreated,
}: {
  readonly onClose: () => void;
  readonly onCreated: () => Promise<void> | void;
}): ReactNode => {
  const session = useSession();
  const toast = useToast();
  const { holds, holdsAny } = useAccess();
  /* Quien no puede escribir sueldos no ve el campo: lo pondrá quien valide. */
  const puedeEditarSueldo = holds('editar_salario');
  /*
   * Un superusuario da de alta en cualquier área; un administrador sólo en la
   * suya. Es la misma división que ya usan las callables de organización, donde
   * `areas` alcanza cualquier área y `admin` sólo la propia — ofrecer en la
   * pantalla lo que el servidor va a rechazar no es generosidad, es una vía
   * muerta con un mensaje de permiso al final.
   */
  const isSuper = session.role === 'super';
  /*
   * El dominio por el listener vivo y no por una consulta puntual: si alguien lo
   * cambia en Configuración global, este formulario deja de ofrecer el viejo sin
   * recargar. Es la misma promesa OTA-safe del resto del portal.
   */
  const config = useRuntimeConfig();
  const domain = config.identity.allowedDomains[0] ?? '';
  const [form, setForm] = useState<FormState>(emptyForm);
  const [selectedArea, setSelectedArea] = useState<AreaId>(session.areaId);
  const [subAreaId, setSubAreaId] = useState<SubAreaId | ''>('');
  const [role, setRole] = useState<Role>('empleado');
  const [positionId, setPositionId] = useState<string>('');
  const [createGoogleAccount, setCreateGoogleAccount] = useState(true);
  /*
   * Los campos que RR.HH. fijó como obligatorios, y que hasta ahora no estaban.
   *
   * Salen de la hoja con la que se dan las altas: modalidad, equipo de cómputo,
   * correo personal, número de contacto, jefe directo y sueldo autorizado. El
   * servidor los exige también — que los pida sólo la pantalla dejaría la regla
   * en el único sitio donde no se puede hacer valer.
   */
  const [workMode, setWorkMode] = useState<WorkMode | ''>('');
  const [computerEquipment, setComputerEquipment] = useState('');
  const [personalEmail, setPersonalEmail] = useState('');
  const [personalPhone, setPersonalPhone] = useState('');
  const [leaderPersonId, setLeaderPersonId] = useState('');
  /* El sueldo se teclea en pesos y viaja en centavos: la coma flotante acumula
     deriva, y la deriva acaba en un finiquito que no cuadra con el recibo. */
  const [monthlySalary, setMonthlySalary] = useState('');
  /*
   * Los dos archivos, en memoria hasta que la ficha exista.
   *
   * No se pueden subir antes: la ruta del bucket lleva el `personId`, y el
   * `personId` lo asigna el servidor al crear la ficha. Guardarlos en una carpeta
   * temporal y moverlos después sería inventar un estado a medias que alguien
   * tendría que limpiar; sostener dos `File` en memoria unos segundos no.
   */
  const [docs, setDocs] = useState<PersonDocs>({});
  const [busy, setBusy] = useState(false);
  /*
   * Se deja de sugerir en cuanto alguien escribe el correo a mano.
   *
   * Sin esto, cada tecla del apellido pisaría lo que la persona acababa de
   * escribir — un campo que se autocompleta y además se autocorrige es un campo
   * que pelea con quien lo usa.
   */
  const emailTouched = useRef(false);

  /*
   * Dónde estaba el cursor cuando se reescribió el campo.
   *
   * Capitalizar en cada tecla obliga a devolver el valor transformado al input, y
   * un input controlado al que le cambian el valor manda el cursor al final —
   * invisible mientras se escribe al final, e insufrible en cuanto alguien vuelve
   * a corregir una letra en medio de «de la Cruz».
   *
   * `capitalizeNamePart` no cambia la longitud, sólo la caja, así que el índice
   * del cursor sigue valiendo y basta con reponerlo tras el commit.
   */
  const caret = useRef<{ el: HTMLInputElement; pos: number } | null>(null);
  useLayoutEffect(() => {
    const pending = caret.current;
    if (pending === null) return;
    caret.current = null;
    pending.el.setSelectionRange(pending.pos, pending.pos);
  });
  const [suggestion, setSuggestion] = useState<EmailSuggestion | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [done, setDone] = useState<{
    email: string;
    note: string | null;
    password: string | null;
  } | null>(null);

  const set = (key: keyof FormState) => (value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
    setFailure(null);
  };

  const areas = useQuery({
    queryKey: ['intake', 'areas', isSuper],
    queryFn: async () => {
      const result = await getServices().organization.listAreas();
      if (!result.ok) return [];
      return result.value.filter(
        (area: Area) => area.active && (isSuper || area.id === session.areaId),
      );
    },
  });

  /*
   * Las sub áreas del área **elegida**, no las del área de quien mira.
   *
   * Venían como prop desde `AdminScreen`, que sólo carga las suyas, así que un
   * superusuario elegía «Académico» y seguía viendo las de Tecnología — ofreciendo
   * una sub área que no existe en esa área. Se vio en pantalla; ningún tipo lo
   * podía atrapar, porque las dos listas son `SubArea[]` y ninguna sabe de qué
   * área es la otra.
   */
  const subAreas = useQuery({
    queryKey: ['intake', 'subAreas', selectedArea],
    queryFn: async () => {
      const result = await getServices().organization.listSubAreas(selectedArea);
      return result.ok ? result.value : [];
    },
  });

  const subAreaList = subAreas.data ?? [];
  /*
   * Vacío no es lo mismo que cargando, y ninguno es «roto».
   *
   * Un área recién creada no tiene sub áreas todavía, y un desplegable con un
   * solo «Elige una…» debajo se lee como una pantalla descompuesta. Es la lección
   * que el roadmap ya lista: cuando alguien dice «no me aparece», la primera
   * pregunta es si esa pantalla puede distinguir vacío de roto.
   */
  const areaHasNoSubAreas = !subAreas.isLoading && subAreaList.length === 0;


  /*
   * El nombre se compone mientras se escribe, y se enseña.
   *
   * Es lo único que vuelve legible una captura en cuatro casillas: quien la llena
   * ve exactamente la cadena que va a quedar guardada, y un segundo apellido en
   * la casilla del segundo nombre se nota al instante en vez de tres meses
   * después en un correo.
   */
  const composed = PersonFullName.create({
    primerNombre: form.primerNombre,
    segundoNombre: form.segundoNombre,
    primerApellido: form.primerApellido,
    segundoApellido: form.segundoApellido,
  });

  // Sólo se acusa lo que ya se tocó: un formulario que abre en rojo regaña por
  // no haber escrito todavía.
  const touchedName = form.primerNombre.length > 0 || form.primerApellido.length > 0;
  const nameError = composed.ok || !touchedName ? null : composed.error;
  const badPart = partOf(nameError);

  /*
   * Propone el correo cuando el nombre ya está completo.
   *
   * El disparador es el nombre compuesto y no cada tecla: mientras el nombre no
   * valide no hay de dónde derivar nada, y probar candidatos a medio escribir
   * gastaría una consulta por letra. `ignore` corta la carrera — si el nombre
   * cambia mientras las consultas van en camino, la respuesta vieja no debe
   * pisar a la nueva.
   */
  const nameKey = composed.ok ? composed.value.displayName : '';
  useEffect(() => {
    if (!composed.ok || emailTouched.current) return;

    let ignore = false;
    const parts = {
      primerNombre: composed.value.primerNombre,
      segundoNombre: composed.value.segundoNombre,
      primerApellido: composed.value.primerApellido,
      segundoApellido: composed.value.segundoApellido,
    };

    setSuggesting(true);
    void suggestEmail(parts, async (candidate): Promise<Availability> => {
      const result = await getServices().people.getByEmail(`${candidate}@${domain}`);
      if (result.ok) return 'taken';
      /*
       * Sólo «no existe» significa libre. Un permiso denegado —las reglas acotan
       * `people` por área— diría «libre» sobre un correo que ya es de alguien.
       */
      return result.error.kind === 'not-found' ? 'free' : 'unknown';
    }).then((next) => {
      if (ignore) return;
      setSuggestion(next);
      setSuggesting(false);
      if (next.suggested !== null) {
        setForm((current) => ({ ...current, emailLocalPart: next.suggested ?? '' }));
      }
    });

    return () => {
      ignore = true;
    };
    // `nameKey` resume las cuatro partes en una cadena: cambia cuando cambia el
    // nombre y no cuando React vuelve a crear el objeto `composed`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameKey, domain]);

  /*
   * A quién se le puede poner de jefe: gente activa del área elegida.
   *
   * Del área y no de la sub área, porque en seis de las ocho áreas todavía no hay
   * sub áreas y filtrar por ellas dejaría la lista vacía justo donde más altas
   * hay. Se pide al elegir área, no al abrir el modal.
   */
  const leaderCandidates = useQuery({
    queryKey: ['intake', 'leaders', selectedArea],
    queryFn: async () => {
      const result = await getServices().people.list(
        { areaId: selectedArea, status: 'active' },
        { limit: 300 },
      );
      return result.ok
        ? [...result.value.items].sort((a, b) => a.displayName.localeCompare(b.displayName, 'es'))
        : [];
    },
  });

  /** Pesos tecleados → centavos enteros. Vacío o ilegible da cero, que no pasa. */
  const monthlyCents = Math.round(Number(monthlySalary.replace(/[^0-9.]/g, '')) * 100);

  const localPart = form.emailLocalPart.trim().toLowerCase();
  const emailPreview = localPart.length === 0 ? '' : `${localPart}@${domain}`;

  /*
   * Lo que falta, y de ahí el botón: una sola respuesta para las dos cosas.
   *
   * El sueldo no entra: es opcional al capturar y obligatorio al validar. Quien
   * sólo captura no ve sueldos, y pedirle uno a ciegas es peor que no pedírselo.
   */
  const gaps = intakeGaps({
    nameOk: composed.ok,
    emailLocalPart: localPart,
    subAreaId,
    positionId,
    hireDate: form.hireDate,
    workMode,
    computerEquipment,
    personalEmail,
    personalPhone,
    leaderPersonId,
  });
  const canSubmit = gaps.length === 0 && !busy && done === null;

  const submit = async (): Promise<void> => {
    if (!composed.ok || subAreaId === '') return;
    setBusy(true);
    setFailure(null);

    const result = await getServices().people.createPerson({
      primerNombre: composed.value.primerNombre,
      segundoNombre: composed.value.segundoNombre,
      primerApellido: composed.value.primerApellido,
      segundoApellido: composed.value.segundoApellido,
      areaId: selectedArea,
      subAreaId,
      role,
      positionId: positionId.length > 0 ? positionId : null,
      hireDate: form.hireDate.length > 0 ? form.hireDate : null,
      emailLocalPart: localPart,
      createGoogleAccount,
      workMode,
      computerEquipment: computerEquipment.trim(),
      personalEmail: personalEmail.trim().toLowerCase(),
      personalPhone: personalPhone.replace(/[^0-9]/g, ''),
      leaderPersonId,
      ...(puedeEditarSueldo && monthlyCents > 0 ? { monthlyCents } : {}),
    });

    /*
     * Los archivos, ahora que la ficha existe.
     *
     * Antes no se podía: la ruta del bucket lleva el `personId` y lo asigna el
     * servidor. Si alguno falla no se deshace el alta — la persona ya está creada,
     * su sueldo también, y tirar todo eso por un PDF sería peor que quedarse sin
     * el PDF. Se dice cuál faltó y se sigue.
     */
    if (result.ok) {
      const fallidos = await subirDocumentos(result.value.personId, selectedArea, docs);
      if (fallidos.length > 0) {
        toast.bad(
          `La ficha se creó, pero no se pudo subir ${fallidos.join(' ni ')}. Súbelo desde su ficha.`,
        );
      }
    }

    setBusy(false);

    if (!result.ok) {
      setFailure(`No se pudo dar de alta.${reasonFor(result.error)}`);
      return;
    }

    /*
     * Se distingue «se creó el buzón» de «se guardó la ficha y el buzón no».
     *
     * Mientras la delegación de dominio no esté autorizada el servidor devuelve
     * `skipped`, y una pantalla que celebrara igual en los dos casos diría que la
     * persona ya puede entrar al portal cuando todavía no tiene con qué.
     */
    setDone({
      email: result.value.email,
      note: result.value.googleAccount === 'created' ? null : result.value.googleAccountNote,
      password: result.value.googleAccountPassword,
    });
    await onCreated();
  };

  const nameField = (part: NamePart, key: keyof FormState, required: boolean): ReactNode => (
    <Field label={`${NAME_PART_LABEL[part]}${required ? '' : ' (opcional)'}`}>
      {(id) => (
        <input
          id={id}
          className={inputClass(badPart === part)}
          value={form[key]}
          autoComplete="off"
          /*
           * `off` y no `words`: la capitalización la hace la misma regla del
           * dominio, y la del teclado no sabe de partículas — pondría «De La
           * Cruz» en móvil y otra cosa en escritorio.
           */
          autoCapitalize="off"
          onChange={(event) => {
            const el = event.target;
            const pos = el.selectionStart ?? el.value.length;
            caret.current = { el, pos };
            set(key)(capitalizeNamePart(el.value));
          }}
        />
      )}
    </Field>
  );

  /*
   * Las acciones van al pie del `Modal`, no dentro del cuerpo.
   *
   * Estaban en el cuerpo, que es el que hace scroll, así que «Dar de alta»
   * quedaba cortado abajo y sólo aparecía al bajar — en un formulario cuyo
   * contenido desborda por 185 px, la acción principal era invisible hasta que
   * alguien adivinara que había que desplazarse. El pie queda fuera del área
   * desplazable y siempre a la vista.
   */
  const footer =
    done !== null ? (
      <Button variant="primary" onClick={onClose}>
        Cerrar
      </Button>
    ) : (
      <>
        {/* Por qué está apagado, junto al botón que lo está. Sin esto alguien con
            el formulario lleno y un dígito de más no tenía por dónde empezar. */}
        {gaps.length > 0 && !busy && (
          <p className={styles.missing} role="status">
            {/* Con el formulario recién abierto falta todo, y enumerarlo es
                ruido: se nombran cuando ya son pocos, que es cuando no se ven. */}
            {gaps.length > NAMED_GAPS ? (
              `Faltan ${String(gaps.length)} datos por llenar.`
            ) : (
              <>
                <strong>{gaps.length === 1 ? 'Falta ' : 'Faltan '}</strong>
                {new Intl.ListFormat('es', { style: 'long', type: 'conjunction' }).format(gaps)}.
              </>
            )}
          </p>
        )}
        <Button variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          disabled={!canSubmit}
          onClick={() => {
            void submit();
          }}
        >
          {busy ? 'Dando de alta…' : 'Dar de alta'}
        </Button>
      </>
    );

  return (
    <Modal title="Dar de alta a una persona" size="full" onClose={onClose} footer={footer}>
      {done !== null ? (
        <div className={styles.body}>
          <Banner tone={done.note === null ? 'success' : 'warn'}>
            {done.note === null
              ? `Listo. ${done.email} ya tiene ficha y cuenta.`
              : `Ficha creada para ${done.email}, pero sin cuenta de correo. ${done.note}`}
          </Banner>

          {/*
            La contraseña temporal, y esta pantalla es el único lugar donde
            existe: no se guarda, no está en la bitácora y el servidor no la
            escribe en su log. Si se cierra sin copiarla hay que restablecerla
            desde la consola de Workspace, así que se dice aquí y no en un
            párrafo de ayuda que nadie lee a tiempo.
          */}
          {done.password !== null && (
            <div className={styles.secret}>
              <span className={styles.secretLabel}>Contraseña temporal</span>
              <code className={styles.secretValue}>{done.password}</code>
              <span className={styles.secretNote}>
                Cópiala ahora y entrégasela: no vuelve a mostrarse en ningún lado. La
                persona tendrá que cambiarla la primera vez que entre.
              </span>
            </div>
          )}
        </div>
      ) : (
        <div className={styles.split}>
          {/*
            Los documentos a la izquierda y los campos a la derecha.
            En una sola columna el formulario medía 1081px y se leía como un rollo;
            partido, cada mitad cabe de un vistazo y la lista de lo que falta queda
            a la vista mientras se captura, que es cuando sirve.
          */}
          <DocumentDrop files={docs} onChange={setDocs} />

          <div className={styles.body}>
          <p className={styles.note}>
            Para quien todavía no tiene cuenta. Si la persona ya inició sesión, aparece en
            «Cuentas por validar» y se aprueba desde ahí.
          </p>

          {/* El nombre se queda en dos columnas aunque el resto vaya a tres: son
              cuatro casillas que forman dos parejas —nombre y apellido— y en una
              rejilla de tres quedan 3+1, con el segundo apellido solo y un hueco
              al lado que no significa nada. */}
          <div className={cx(styles.grid, styles.gridPairs)}>
            {nameField('primerNombre', 'primerNombre', true)}
            {nameField('segundoNombre', 'segundoNombre', false)}
            {nameField('primerApellido', 'primerApellido', true)}
            {nameField('segundoApellido', 'segundoApellido', false)}
          </div>

          {/* El nombre armado, en vivo. Es lo que vuelve legible la captura partida. */}
          <div className={styles.preview}>
            <span className={styles.previewLabel}>Se guardará como</span>
            <span className={styles.previewValue}>
              {composed.ok ? composed.value.displayName : '—'}
            </span>
          </div>

          {nameError !== null && (
            <Banner tone="warn">{messageFor(nameError)}</Banner>
          )}

          <div className={styles.grid}>
            <Field label="Área">
              {(id) => (
                <select
                  id={id}
                  className={selectClass()}
                  value={selectedArea}
                  onChange={(event) => {
                    setSelectedArea(event.target.value as AreaId);
                    // Las sub áreas son de la otra área: conservarla elegiría una
                    // que no existe ahí.
                    setSubAreaId('');
                  }}
                >
                  {(areas.data ?? []).map((area) => (
                    <option key={area.id} value={area.id}>
                      {area.name}
                    </option>
                  ))}
                </select>
              )}
            </Field>

            {/* `exactOptionalPropertyTypes`: la clave se pone o no se pone, y
                pasarla como `undefined` no es lo mismo que omitirla. */}
            <Field
              label="Sub área"
              {...(areaHasNoSubAreas
                ? { hint: 'Esta área todavía no tiene sub áreas. Créalas primero en Sub áreas y líderes.' }
                : {})}
            >
              {(id) => (
                <select
                  id={id}
                  className={selectClass()}
                  value={subAreaId}
                  disabled={subAreas.isLoading || areaHasNoSubAreas}
                  onChange={(event) => {
                    setSubAreaId(event.target.value as SubAreaId);
                  }}
                >
                  <option value="">
                    {subAreas.isLoading
                      ? 'Cargando…'
                      : areaHasNoSubAreas
                        ? 'Esta área no tiene sub áreas'
                        : 'Elige una…'}
                  </option>
                  {subAreaList.map((sub) => (
                    <option key={sub.id} value={sub.id}>
                      {sub.name}
                    </option>
                  ))}
                </select>
              )}
            </Field>

            <Field label="Rol">
              {(id) => (
                <select
                  id={id}
                  className={selectClass()}
                  value={role}
                  onChange={(event) => {
                    setRole(event.target.value as Role);
                  }}
                >
                  {/* Sin `super`: un superusuario configura la empresa y no se da
                      de alta como parte de un área. */}
                  {ROLES.filter((r) => r !== 'super').map((r) => (
                    <option key={r} value={r}>
                      {ROLE_LABEL[r]}
                    </option>
                  ))}
                </select>
              )}
            </Field>

            {/* Un buscador y no una lista desplegable: son 322 puestos. Los
                retirados no se ofrecen —el catálogo apaga en vez de borrar,
                porque sigue habiendo gente con ellos—, y el que falte se agrega
                ahí mismo. Ver `PositionPicker`. */}
            <Field label="Puesto">
              {(id) => (
                <PositionPicker
                  id={id}
                  positions={config.catalogs.positions}
                  value={positionId}
                  onChange={setPositionId}
                  canAdd={holdsAny(POSITION_ADD_PERMISSIONS)}
                />
              )}
            </Field>

            <Field label="Jefe directo" hint="De aquí sale quién le firma sus vacaciones.">
              {(id) => (
                <select
                  id={id}
                  className={selectClass()}
                  value={leaderPersonId}
                  disabled={leaderCandidates.isPending}
                  onChange={(event) => {
                    setLeaderPersonId(event.target.value);
                  }}
                >
                  <option value="">
                    {leaderCandidates.isPending ? 'Cargando…' : 'Elige a quién le reporta'}
                  </option>
                  {(leaderCandidates.data ?? []).map((persona) => (
                    <option key={persona.id} value={persona.id}>
                      {persona.displayName}
                    </option>
                  ))}
                </select>
              )}
            </Field>

            <Field label="Modalidad">
              {(id) => (
                <select
                  id={id}
                  className={selectClass()}
                  value={workMode}
                  onChange={(event) => {
                    setWorkMode(event.target.value as WorkMode | '');
                  }}
                >
                  <option value="">Elige una…</option>
                  {WORK_MODES.map((modo) => (
                    <option key={modo} value={modo}>
                      {WORK_MODE_LABEL[modo]}
                    </option>
                  ))}
                </select>
              )}
            </Field>

            <Field label="Equipo de cómputo" hint="Lo que se le entrega. Es lo que hay que recoger el día de la baja.">
              {(id) => (
                <input
                  id={id}
                  className={inputClass()}
                  placeholder='MacBook Pro 14"'
                  value={computerEquipment}
                  onChange={(event) => {
                    setComputerEquipment(event.target.value);
                  }}
                />
              )}
            </Field>

            {/*
              El sueldo, obligatorio y en pesos.
              Se teclea en pesos y viaja en centavos: una cifra en coma flotante
              acumula deriva, y la deriva termina en un finiquito que no cuadra
              con el recibo. Vive en otra colección y con otro permiso — la ficha
              no lo lleva dentro.
            */}
            {/*
              El sueldo sólo aparece para quien puede escribirlo.
              Quien captura sin `editar_salario` no lo ve, y no es un hueco: lo
              pone quien valida, que es donde el campo pasó a ser obligatorio.
              Enseñar un campo que la callable va a rechazar sería una vía muerta
              con un mensaje de permiso al final.
            */}
            {puedeEditarSueldo && (
              <Field
                label="Sueldo autorizado"
                hint="Mensual bruto. Si no lo pones, lo pedirá quien valide el alta."
              >
                {(id) => (
                  <input
                    id={id}
                    className={inputClass()}
                    inputMode="decimal"
                    placeholder="18500"
                    value={monthlySalary}
                    onChange={(event) => {
                      setMonthlySalary(event.target.value);
                    }}
                  />
                )}
              </Field>
            )}

            <Field label="Correo personal" hint="El suyo, no el de la empresa. Por si deja de tener el de trabajo.">
              {(id) => (
                <input
                  id={id}
                  className={inputClass()}
                  type="email"
                  placeholder="nombre@gmail.com"
                  value={personalEmail}
                  onChange={(event) => {
                    setPersonalEmail(event.target.value);
                  }}
                />
              )}
            </Field>

            <Field label="Número de contacto" hint="Diez dígitos." error={phoneError(personalPhone)}>
              {(id) => (
                <input
                  id={id}
                  className={inputClass()}
                  inputMode="tel"
                  placeholder="5511942208"
                  value={personalPhone}
                  onChange={(event) => {
                    setPersonalPhone(event.target.value);
                  }}
                />
              )}
            </Field>

            <Field label="Fecha de ingreso" hint="Determina su antigüedad y sus días.">
              {(id) => (
                <input
                  id={id}
                  type="date"
                  className={inputClass()}
                  value={form.hireDate}
                  onChange={(event) => {
                    set('hireDate')(event.target.value);
                  }}
                />
              )}
            </Field>

            {/*
              El correo ocupa la media celda que dejaba libre la fecha.
              Eran cinco campos en una rejilla de dos columnas, así que la fecha
              se quedaba sola en su fila y al lado había un hueco del tamaño de un
              campo entero, en mitad del formulario. Fuera de la rejilla el correo
              costaba una fila completa más su hueco entre bloques.
            */}
            <Field
              label="Correo corporativo"
              hint="El dominio no se escribe: sale de la configuración de la empresa, el mismo que exige el inicio de sesión."
            >
              {(id) => (
                <div className={styles.emailRow}>
                  <input
                    id={id}
                    className={inputClass()}
                    value={form.emailLocalPart}
                    placeholder="nombre.apellido"
                    autoComplete="off"
                    onChange={(event) => {
                      emailTouched.current = true;
                      set('emailLocalPart')(event.target.value);
                    }}
                  />
                  <span className={styles.emailDomain}>@{domain}</span>
                </div>
              )}
            </Field>
          </div>

          {emailPreview.length > 0 && (
            <div className={styles.preview}>
              <span className={styles.previewLabel}>Su correo será</span>
              <span className={styles.previewValue}>{emailPreview}</span>
            </div>
          )}

          {suggesting && <p className={styles.hint}>Buscando un correo libre…</p>}

          {/*
            * Por qué no se eligió el patrón de la casa.
            *
            * Enseñar sólo la alternativa dejaría a quien captura preguntándose si
            * el sistema se equivocó de nombre. Decir «jose.lugardo ya es de
            * alguien» convierte una sorpresa en una explicación.
            */}
          {suggestion !== null && suggestion.taken.length > 0 && (
            <p className={styles.hint}>
              {suggestion.taken.length === 1
                ? `${suggestion.taken[0] ?? ''}@${domain} ya es de alguien.`
                : `Ya están tomados: ${suggestion.taken.join(', ')}.`}
            </p>
          )}

          {/* Las otras libres, pulsables. */}
          {suggestion !== null && suggestion.free.length > 1 && (
            <div className={styles.alternatives}>
              <span className={styles.previewLabel}>Otras libres</span>
              <div className={styles.chips}>
                {suggestion.free
                  .filter((candidate) => candidate !== localPart)
                  .map((candidate) => (
                    <button
                      key={candidate}
                      type="button"
                      className={styles.chip}
                      onClick={() => {
                        emailTouched.current = true;
                        set('emailLocalPart')(candidate);
                      }}
                    >
                      {candidate}
                    </button>
                  ))}
              </div>
            </div>
          )}

          {/*
            * `incomplete` no se calla.
            *
            * Una comprobación que no se pudo hacer se ve igual que una que salió
            * bien si nadie la distingue, y ésa es la forma que este proyecto ya
            * pagó cinco veces. El servidor vuelve a comprobar al crear, así que
            * esto no es un riesgo de datos — es no dejar creer que sí se revisó.
            */}
          {suggestion !== null && suggestion.incomplete && (
            <Banner tone="warn">
              No se pudieron comprobar todos los correos. Al dar de alta, el servidor
              vuelve a revisarlo y rechaza el alta si ya existe.
            </Banner>
          )}

          {suggestion !== null && suggestion.suggested === null && !suggesting && (
            <Banner tone="warn">
              No se encontró ningún correo libre derivado de ese nombre. Escribe uno a
              mano.
            </Banner>
          )}

          <label className={styles.check}>
            <input
              type="checkbox"
              checked={createGoogleAccount}
              onChange={(event) => {
                setCreateGoogleAccount(event.target.checked);
              }}
            />
            <span>
              <span className={styles.checkLabel}>Crear también su cuenta de Google</span>
              <span className={styles.checkNote}>
                Consume una licencia de Workspace. Sin ella la persona tiene ficha y saldo,
                pero no puede iniciar sesión.
              </span>
            </span>
          </label>

          {failure !== null && <Banner tone="danger">{failure}</Banner>}

          </div>
        </div>
      )}
    </Modal>
  );
};


/**
 * Qué archivo se cargó para cada documento. Vacío es «todavía ninguno».
 *
 * Un mapa parcial y no ocho campos: la lista la manda `PERSON_DOCUMENTS`, y con
 * campos fijos habría que tocar este tipo cada vez que RR.HH. añada o quite un
 * papel — que es exactamente lo que acaba de pasar, de dos documentos a ocho.
 */
export type PersonDocs = Partial<Record<PersonDocumentKind, File>>;

/**
 * La columna de documentos: arrastrar, soltar, y ver qué falta.
 *
 * **Lo que falta se enseña siempre, no sólo al fallar.** Es la mitad del valor de
 * esta columna: son ocho papeles y nadie los recuerda de memoria; una zona de
 * arrastre vacía no dice cuáles son.
 *
 * **Cada renglón acepta su propio archivo**, además de la zona de arriba. Con dos
 * documentos bastaba con ir llenando huecos en orden; con ocho, soltar un PDF y
 * que caiga en «Acta de nacimiento» porque era el primer hueco libre es adivinar
 * mal la mayoría de las veces. La zona grande sigue existiendo para el caso
 * cómodo —soltar de uno en uno— y cada renglón para poner cada cosa en su sitio.
 *
 * No se adivina por el nombre del archivo: sería más listo y fallaría con
 * «Escaneado 2026-08-24.pdf», que es como se llaman de verdad.
 */
const DocumentDrop = ({
  files,
  onChange,
}: {
  readonly files: PersonDocs;
  readonly onChange: (next: PersonDocs) => void;
}): ReactNode => {
  const [over, setOver] = useState<PersonDocumentKind | 'zona' | null>(null);
  const [rechazado, setRechazado] = useState<string | null>(null);

  const soloPdf = (lista: readonly File[]): readonly File[] => {
    const pdfs = lista.filter((file) => file.type === 'application/pdf');
    setRechazado(pdfs.length === lista.length ? null : 'Sólo PDF: lo demás no se guardó.');
    return pdfs;
  };

  /** Desde la zona grande: a los huecos libres, en el orden de la lista. */
  const repartir = (lista: readonly File[]): void => {
    const siguiente: PersonDocs = { ...files };
    for (const file of soloPdf(lista)) {
      const hueco = PERSON_DOCUMENTS.find((doc) => siguiente[doc.key] === undefined);
      if (hueco === undefined) break;
      siguiente[hueco.key] = file;
    }
    onChange(siguiente);
  };

  /** Desde un renglón: a ese documento y a ninguno más. */
  const asignar = (kind: PersonDocumentKind, lista: readonly File[]): void => {
    const [file] = soloPdf(lista);
    if (file === undefined) return;
    onChange({ ...files, [kind]: file });
  };

  const faltan = PERSON_DOCUMENTS.filter(
    (doc) => doc.required && files[doc.key] === undefined,
  ).length;

  return (
    <aside className={styles.docs}>
      <div className={styles.docsHead}>
        <h3 className={styles.docsTitle}>Documentos</h3>
        <span className={cx(styles.docsCount, faltan === 0 && styles.docsCountDone)}>
          {faltan === 0 ? 'completos' : `faltan ${String(faltan)}`}
        </span>
      </div>

      <label
        className={cx(styles.dropZone, over === 'zona' && styles.dropZoneOver)}
        onDragOver={(event) => {
          event.preventDefault();
          setOver('zona');
        }}
        onDragLeave={() => {
          setOver(null);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setOver(null);
          repartir([...event.dataTransfer.files]);
        }}
      >
        <span className={styles.dropGlyph} aria-hidden="true">
          ↓
        </span>
        <span className={styles.dropText}>
          Arrastra los PDF aquí
          <span className={styles.dropHint}>van a los que falten, en orden</span>
        </span>
        <input
          type="file"
          accept="application/pdf"
          multiple
          className={styles.dropInput}
          onChange={(event) => {
            repartir([...(event.target.files ?? [])]);
            /* Se limpia para que elegir el mismo archivo dos veces vuelva a
               disparar el evento: sin esto, corregir un error obliga a elegir
               otro archivo primero. */
            event.target.value = '';
          }}
        />
      </label>

      {rechazado !== null && <p className={styles.docsWarn}>{rechazado}</p>}

      <ul className={styles.docsList}>
        {PERSON_DOCUMENTS.map((doc) => {
          const file = files[doc.key];
          return (
            <li key={doc.key}>
              <label
                className={cx(
                  styles.doc,
                  file !== undefined && styles.docReady,
                  over === doc.key && styles.docOver,
                )}
                onDragOver={(event) => {
                  event.preventDefault();
                  setOver(doc.key);
                }}
                onDragLeave={() => {
                  setOver(null);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  setOver(null);
                  asignar(doc.key, [...event.dataTransfer.files]);
                }}
              >
                <span className={styles.docMark} aria-hidden="true">
                  {file === undefined ? '·' : '✓'}
                </span>
                <span className={styles.docText}>
                  <span className={styles.docName}>
                    {doc.label}
                    {!doc.required && <span className={styles.docOptional}> · opcional</span>}
                  </span>
                  <span className={styles.docFile}>
                    {file === undefined ? 'Falta' : file.name}
                  </span>
                </span>
                <input
                  type="file"
                  accept="application/pdf"
                  className={styles.dropInput}
                  onChange={(event) => {
                    asignar(doc.key, [...(event.target.files ?? [])]);
                    event.target.value = '';
                  }}
                />
              </label>
              {file !== undefined && (
                <button
                  type="button"
                  className={styles.docRemove}
                  aria-label={`Quitar ${doc.label}`}
                  onClick={() => {
                    const siguiente = { ...files };
                    delete siguiente[doc.key];
                    onChange(siguiente);
                  }}
                >
                  Quitar
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {/* Se dice aquí y no al fallar: el alta se guarda igual, y quien valida es
          quien va a echarlos en falta. */}
      <p className={styles.docsNote}>
        Si falta alguno, el alta se guarda igual y queda anotado para quien la valide.
      </p>
    </aside>
  );
};

/**
 * Sube lo que haya, y devuelve qué no pudo.
 *
 * No corta al primer fallo: si el formato falla por tamaño, el expediente todavía
 * puede entrar, y quien captura prefiere tener uno de dos a tener cero por un
 * error del otro.
 */
const subirDocumentos = async (
  personId: string,
  areaId: AreaId,
  docs: PersonDocs,
): Promise<readonly string[]> => {
  const fallidos: string[] = [];

  for (const doc of PERSON_DOCUMENTS) {
    const file = docs[doc.key];
    if (file === undefined) continue;

    const subida = await getServices().documents.uploadPersonDocument({
      file,
      fileName: file.name,
      ownerPersonId: asId<'PersonId'>(personId),
      areaId,
      recordId: doc.key,
    });
    if (!subida.ok) {
      fallidos.push(doc.label.toLowerCase());
      continue;
    }

    /*
     * Y se anota en la ficha. Sin este paso el archivo queda en el bucket y no lo
     * encuentra nadie: la ficha es lo que dice cuál de los ocho documentos es
     * cada archivo y quién lo subió. Un PDF huérfano se ve exactamente igual que
     * uno que nunca se subió.
     */
    const enlace = await getServices().people.attachDocument({
      personId: asId<'PersonId'>(personId),
      kind: doc.key,
      storagePath: subida.value,
    });
    if (!enlace.ok) fallidos.push(doc.label.toLowerCase());
  }

  return fallidos;
};
